import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  completeLesson, getSubmissionAttachmentUrl, gradeSubmission, submitAssignment,
  uploadLessonFile, uploadSubmissionFile,
} from '@/lib/api';
import { dueState, kindLabel, submissionFor } from '@/lib/courseRoom';
import { normalizeRichText } from '@/lib/richText';
import CourseResources from '@/components/course/CourseResources';
import LessonVideoPlayer from '@/components/course/LessonVideoPlayer';
import LessonMaterials from '@/components/course/LessonMaterials';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Award, CheckCircle, ChevronDown, ChevronUp, ClipboardList, Download,
  Loader2, Paperclip, Pencil, Send, Upload, X,
} from 'lucide-react';

const TONE_CLASS = {
  overdue: 'text-rose-600',
  soon: 'text-amber-600',
  upcoming: 'text-[#062B1A]/50',
  done: 'text-[#062B1A]/50',
  none: 'text-[#062B1A]/40',
};

/** Rich instructions, falling back to the plain text older records hold. */
function Body({ item }) {
  if (item.body_html) {
    // Sanitized server-side on every write, so what comes back is safe to render.
    // normalizeRichText only rewraps loose paragraphs; it adds no new markup.
    return (
      <div
        className="prose prose-sm max-w-none text-[#062B1A]/80"
        dangerouslySetInnerHTML={{ __html: normalizeRichText(item.body_html) }}
      />
    );
  }
  const plain = item.content || item.instructions || item.description || '';
  return (
    <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#062B1A]/80">
      {plain || 'No description was added.'}
    </div>
  );
}

/**
 * One piece of classwork, opened.
 *
 * The same panel serves all three kinds and both audiences: it shows what the
 * item is, the files that come with it, and then whatever acting on it means —
 * marking a material done, handing an assignment in, sitting a quiz, or, for
 * the instructor, marking what everyone handed in.
 */
export default function ClassworkDetail({
  courseId, item, submissions, students, completedLessons, isEditor, basePath = '/instructor',
  preview = false, onEdit, onChanged,
}) {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [showMarking, setShowMarking] = useState(false);
  const [marks, setMarks] = useState({});
  const [uploadPercent, setUploadPercent] = useState(null);
  // Files staged for this submission. They are uploaded straight away but only
  // recorded against a submission when the student actually hands in.
  const [attachments, setAttachments] = useState([]);
  const [attaching, setAttaching] = useState(null);

  if (!item) {
    return (
      <div className="py-16 text-center text-sm text-[#062B1A]/45">
        {isEditor ? 'Create a material, assignment or quiz to get started.' : 'Select an item to get started.'}
      </div>
    );
  }

  const submission = submissionFor(submissions, item.id);
  const due = item.kind !== 'material' ? dueState(item.due_date, { submitted: !!submission }) : null;
  const completed = completedLessons.includes(item.id);
  const studentName = (id) => (students || []).find((s) => s.id === id)?.name || 'Student';
  // A locked assignment stops accepting work once the deadline has passed.
  const locked = item.kind === 'assignment' && item.lock_after_due && due?.tone === 'overdue';

  const handleComplete = async () => {
    try {
      setBusy(true);
      await completeLesson({ lesson_id: item.id, course_id: courseId });
      toast.success('Marked complete');
      onChanged?.();
    } catch {
      toast.error('Could not mark this complete');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() && !attachments.length) {
      toast.error('Write your submission or attach a file first');
      return;
    }
    try {
      setBusy(true);
      await submitAssignment({ assignment_id: item.id, content, attachments });
      toast.success('Submitted');
      setContent('');
      setAttachments([]);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally {
      setBusy(false);
    }
  };

  const handleAttach = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setAttaching({ name: file.name, percent: 0 });
      const attachment = await uploadSubmissionFile(item.id, file, {
        onProgress: (percent) => setAttaching((prev) => (prev ? { ...prev, percent } : prev)),
      });
      setAttachments((prev) => [...prev, attachment]);
      toast.success(`${file.name} attached`);
    } catch (err) {
      // Say why. A bare "Upload failed" leaves the student with nothing to act on.
      toast.error(err?.response?.data?.detail || err.message || 'Could not attach that file');
    } finally {
      setAttaching(null);
    }
  };

  const openAttachment = async (submissionId, index) => {
    try {
      const { data } = await getSubmissionAttachmentUrl(submissionId, index);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open that file');
    }
  };

  const handleMark = async (row) => {
    const draft = marks[row.id] || {};
    const score = Number(draft.score ?? row.score ?? 0);
    if (Number.isNaN(score) || score < 0 || score > item.total_marks) {
      toast.error(`Score must be between 0 and ${item.total_marks}`);
      return;
    }
    try {
      setBusy(true);
      await gradeSubmission(row.id, { score, feedback: draft.feedback ?? row.feedback ?? '' });
      toast.success(`Marked ${studentName(row.student_id)}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that mark');
    } finally {
      setBusy(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploadPercent(0);
      await uploadLessonFile(item.id, file, { kind: 'video', onProgress: setUploadPercent });
      toast.success('Video uploaded');
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadPercent(null);
    }
  };

  const handed = isEditor ? (submissions || []).filter((s) => s.reference_id === item.id) : [];
  const ungraded = handed.filter((s) => s.status !== 'graded').length;

  return (
    <div>
      {item.kind === 'material' && <LessonVideoPlayer lesson={item} />}

      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                {item.title}
              </h3>
              <Badge variant="outline" className="text-[10px]">{kindLabel(item.kind)}</Badge>
              {isEditor && item.status !== 'published' && (
                <Badge className="border-0 bg-amber-100 text-[10px] text-amber-800">Draft</Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-4 text-xs">
              {due && <span className={TONE_CLASS[due.tone]}>{due.label}</span>}
              {item.kind !== 'material' && (
                <span className="flex items-center gap-1 text-[#062B1A]/45">
                  <Award className="size-3" />{item.total_marks} points
                </span>
              )}
              {item.kind === 'assignment' && item.grade_category && (
                <span className="text-[#062B1A]/45">{item.grade_category}</span>
              )}
              {item.kind === 'quiz' && (
                <span className="text-[#062B1A]/45">{item.duration_minutes} min</span>
              )}
            </div>
          </div>
          {isEditor && (
            <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid="edit-classwork">
              <Pencil className="mr-2 size-3.5" />Edit
            </Button>
          )}
        </div>

        <Body item={item} />

        {/* Files and links that come with this item */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
            Attachments
          </p>
          <CourseResources
            courseId={courseId}
            itemId={item.id}
            editable={isEditor}
            compact
            emptyLabel={isEditor ? 'Nothing attached yet.' : 'No files came with this.'}
          />
          {item.kind === 'material' && (item.materials || []).length > 0 && (
            <div className="mt-2">
              <LessonMaterials lessonId={item.id} materials={item.materials} />
            </div>
          )}
        </div>

        {isEditor && item.kind === 'material' && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[rgba(6,43,26,0.15)] px-3 py-2 text-sm font-medium text-[#062B1A] hover:bg-[#F7F8FA]">
              <Upload className="size-4" />
              {item.video_type === 'upload' ? 'Replace video' : 'Upload a clip'}
              <input type="file" accept=".mp4,.webm,.mov" className="hidden" onChange={handleVideoUpload} />
            </label>
            <span className="text-xs text-[#062B1A]/40">MP4, WebM or MOV · up to 500 MB</span>
            {uploadPercent !== null && (
              <div className="w-full space-y-1">
                <Progress value={uploadPercent} className="h-2" />
                <p className="text-xs text-[#062B1A]/45">Uploading… {uploadPercent}%</p>
              </div>
            )}
          </div>
        )}

        {preview && item.kind !== 'material' && (
          <p className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 text-xs text-[#062B1A]/60">
            Preview — a student would act on this here. Nothing is recorded while previewing.
          </p>
        )}

        {/* What acting on this item means */}
        {!isEditor && item.kind === 'material' && (
          completed ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="size-4" />
              <span className="text-sm font-medium">Completed</span>
            </div>
          ) : (
            // Inert in preview: this is a way of looking at the course, and it
            // must never write a completion against whoever is looking.
            <Button
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
              disabled={busy || preview}
              title={preview ? 'Disabled while previewing' : undefined}
              onClick={handleComplete}
            >
              <CheckCircle className="mr-2 size-4" />Mark as complete
            </Button>
          )
        )}

        {!isEditor && item.kind === 'assignment' && (
          submission ? (
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-4">
              {submission.status === 'graded' ? (
                <>
                  <p className="text-sm font-semibold text-[#062B1A]">
                    Marked {submission.score}/{submission.max_score ?? item.total_marks}
                  </p>
                  {submission.feedback && <p className="mt-1 text-sm text-[#062B1A]/70">{submission.feedback}</p>}
                </>
              ) : (
                <p className="flex items-center gap-2 text-sm text-blue-700">
                  <CheckCircle className="size-4" />Handed in — awaiting your mark
                </p>
              )}
              {(submission.attachments || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {(submission.attachments || []).map((file, index) => (
                    <button
                      key={file.key || index}
                      type="button"
                      className="flex items-center gap-2 text-xs text-[#0A3B24] underline underline-offset-2 hover:text-[#D4AF37]"
                      onClick={() => openAttachment(submission.id, index)}
                    >
                      <Download className="size-3" />{file.filename || 'Attachment'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : locked ? (
            <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              The deadline has passed and this assignment no longer accepts submissions.
            </p>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#062B1A]/70" htmlFor={`submission-${item.id}`}>
                Your submission
              </label>
              <Textarea
                id={`submission-${item.id}`}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your submission here..."
                className="min-h-[140px]"
                data-testid="assignment-submission-textarea"
              />

              {/* A brief that invites a document has to accept one. */}
              <div className="space-y-2">
                {attachments.map((file, index) => (
                  <div key={file.key} className="flex items-center gap-2 rounded-md border border-[#062B1A]/10 bg-[#F7F8FA] px-3 py-2">
                    <Paperclip className="size-3.5 shrink-0 text-[#062B1A]/40" />
                    <span className="min-w-0 flex-1 truncate text-xs text-[#062B1A]/75">{file.filename}</span>
                    <button
                      type="button"
                      className="rounded p-1 text-[#062B1A]/35 hover:text-rose-600"
                      aria-label={`Remove ${file.filename}`}
                      onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
                {attaching && (
                  <div className="space-y-1">
                    <Progress value={attaching.percent} className="h-2" />
                    <p className="text-xs text-[#062B1A]/45">Attaching {attaching.name}… {attaching.percent}%</p>
                  </div>
                )}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-[rgba(6,43,26,0.25)] px-3 py-2 text-sm font-medium text-[#062B1A] hover:bg-[#F7F8FA]">
                  {attaching ? <Loader2 className="size-4 animate-spin" /> : <Paperclip className="size-4" />}
                  Attach a file
                  <input
                    type="file"
                    className="hidden"
                    disabled={!!attaching || busy || preview}
                    onChange={handleAttach}
                    data-testid="submission-file-input"
                  />
                </label>
                <p className="text-xs text-[#062B1A]/40">
                  PDF, Word, Excel, PowerPoint, images, CSV, TXT or ZIP · up to 50 MB each
                </p>
              </div>

              <Button
                className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                disabled={busy || !!attaching || preview}
                title={preview ? 'Disabled while previewing' : undefined}
                onClick={handleSubmit}
              >
                <Send className="mr-2 size-4" />Hand in
              </Button>
            </div>
          )
        )}

        {!isEditor && item.kind === 'quiz' && (
          submission ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-semibold text-[#062B1A]">
                Scored {submission.score}/{submission.max_score ?? item.total_marks}
              </p>
              <Button size="sm" variant="outline" onClick={() => navigate(`/student/exams/${item.id}/review`)}>
                Review answers
              </Button>
            </div>
          ) : (
            <Button
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
              disabled={preview}
              title={preview ? 'Disabled while previewing' : undefined}
              onClick={() => navigate(`/student/exams/${item.id}/take`)}
            >
              <ClipboardList className="mr-2 size-4" />Start quiz
            </Button>
          )
        )}

        {isEditor && item.kind === 'quiz' && (
          <Button
            size="sm"
            variant="outline"
            data-testid="edit-questions"
            onClick={() => navigate(`${basePath}/exams?exam=${item.id}`)}
          >
            <ClipboardList className="mr-2 size-4" />
            Edit questions ({(item.questions || []).length})
          </Button>
        )}

        {/* Marking, alongside the work it marks */}
        {isEditor && item.kind !== 'material' && (
          <div className="border-t border-[#062B1A]/5 pt-4">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 text-left"
              onClick={() => setShowMarking((v) => !v)}
            >
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/55">
                Submissions ({handed.length})
                {ungraded > 0 && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-amber-800">
                    {ungraded} to mark
                  </span>
                )}
              </span>
              {showMarking ? <ChevronUp className="size-4 text-[#062B1A]/40" /> : <ChevronDown className="size-4 text-[#062B1A]/40" />}
            </button>

            {showMarking && (
              <div className="mt-3 space-y-2">
                {handed.length === 0 && <p className="py-3 text-xs text-[#062B1A]/40">Nothing handed in yet.</p>}
                {handed.map((row) => {
                  const draft = marks[row.id] || {};
                  return (
                    <div key={row.id} className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-[#062B1A]">{studentName(row.student_id)}</p>
                        <Badge className={`border-0 text-[10px] ${row.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {row.status === 'graded' ? `Marked ${row.score}/${row.max_score ?? item.total_marks}` : 'Awaiting mark'}
                        </Badge>
                      </div>
                      {row.content && (
                        <p className="mt-2 whitespace-pre-wrap rounded bg-white p-2 text-xs text-[#062B1A]/70">{row.content}</p>
                      )}
                      {(row.attachments || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-3">
                          {(row.attachments || []).map((file, index) => (
                            <button
                              key={file.key || index}
                              type="button"
                              className="flex items-center gap-1.5 text-xs text-[#0A3B24] underline underline-offset-2 hover:text-[#D4AF37]"
                              onClick={() => openAttachment(row.id, index)}
                            >
                              <Download className="size-3" />{file.filename || 'Attachment'}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 grid gap-2 sm:grid-cols-[90px_1fr_auto] sm:items-start">
                        <Input
                          type="number"
                          min="0"
                          max={item.total_marks}
                          aria-label={`Score for ${studentName(row.student_id)}`}
                          placeholder={`/ ${item.total_marks}`}
                          value={draft.score ?? row.score ?? ''}
                          onChange={(e) => setMarks((p) => ({ ...p, [row.id]: { ...draft, score: e.target.value } }))}
                        />
                        <Textarea
                          aria-label={`Feedback for ${studentName(row.student_id)}`}
                          placeholder="Feedback"
                          className="min-h-[72px]"
                          value={draft.feedback ?? row.feedback ?? ''}
                          onChange={(e) => setMarks((p) => ({ ...p, [row.id]: { ...draft, feedback: e.target.value } }))}
                        />
                        <Button size="sm" className="bg-[#062B1A] text-white hover:bg-[#041B10]" disabled={busy} onClick={() => handleMark(row)}>
                          Save mark
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
